import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CadastroHeader from '../../components/CadastroHeader/CadastroHeader';
import useAlocacoes from './useAlocacoes';
import { usePermissao } from '../../app/hooks/UsePermissao';
import SemPermissao from '../../components/SemPermissao/SemPermissao';
import { styles } from './AlocacoesPage.styles';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import FiltrosTrens from '../../components/FiltrosTrens/FiltrosTrens';
import AppSnackbar from '../../components/AppSnackbar/AppSnackbar';
import TrainIcon from '@mui/icons-material/Train';

/** Status exibidos como colunas dinâmicas */
function filterStatusColumns(statusList) {
  return statusList.filter((s) => {
    const l = s.nome.toLowerCase();
    if (l.startsWith('em ') && !l.includes('manu')) return false;
    if (l.includes('inop')) return false;
    return true;
  });
}

const ALOCACOES_SUBTITLE =
  'Defina a linha e o status operacional de cada trem.';

export default function AlocacoesPage() {
  const navigate = useNavigate();
  const { temPermissao } = usePermissao();
  const temAcesso = temPermissao("Trem.Mover");
  const {
    loading,
    error,
    linhas,
    statusList,
    alocacoes,
    filteredTrens,
    filtroLinha,
    filtroStatus,
    setFiltroLinha,
    setFiltroStatus,
    toggleField,
    selectUnico,
    handleConfirmar,
    snackbar,
    closeSnackbar,
    retry,
  } = useAlocacoes({ enabled: temAcesso });

  const statusColumns = useMemo(() => filterStatusColumns(statusList), [statusList]);

  if (!temAcesso) {
    return (
      <Box sx={styles.pageWrapper}>
        <Box component="main" sx={styles.mainContent}>
          <Paper elevation={0} sx={styles.card}>
            <CadastroHeader
              title="Alocações"
              subtitle={ALOCACOES_SUBTITLE}
              backRoute="/dashboard"
            />
            <SemPermissao mensagem="Você não tem permissão para acessar a alocação de trens." />
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.pageWrapper}>

        <Box component="main" sx={styles.mainContent}>
          {error && (
            <Alert
              severity="error"
              sx={styles.alertError}
              action={
                <Button color="inherit" size="small" onClick={retry}>
                  Tentar novamente
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Paper elevation={0} sx={styles.card}>
            <CadastroHeader
              title="Alocações"
              subtitle={ALOCACOES_SUBTITLE}
              backRoute="/dashboard"
            />

            {/* Filtros */}
            <FiltrosTrens
              linhas={linhas.map((l) => ({ value: l.nome, label: `Linha ${l.nome}` }))}
              statusList={statusList.map((s) => ({ value: s.nome, label: s.nome }))}
              filtroLinha={filtroLinha}
              setFiltroLinha={setFiltroLinha}
              filtroStatus={filtroStatus}
              setFiltroStatus={setFiltroStatus}
              mostrarSemOpcoes
            />

            {/* Tabela para marcar os dados */}
            {loading ? (
              <Box sx={styles.loadingBox}>
                <CircularProgress sx={styles.progress} />
              </Box>
            ) : (
              <TableContainer sx={styles.tableContainer}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={styles.thFirst} />
                      <TableCell sx={styles.thCenter}>
                        <Box sx={styles.thTitleBox}>
                          <span>Em Operação</span>
                          <Box component="span" sx={styles.thSubText}>Linha 8</Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.thCenter}>
                        <Box sx={styles.thTitleBox}>
                          <span>Em Operação</span>
                          <Box component="span" sx={styles.thSubText}>Linha 9</Box>
                        </Box>
                      </TableCell>
                      {statusColumns.map((s) => (
                        <TableCell key={s.id} sx={styles.thCenter}>{s.nome}</TableCell>
                      ))}
                      <TableCell sx={styles.thCenter}>
                        <Box sx={styles.thTitleBox}>
                          <span>Inoperante</span>
                          <Box component="span" sx={styles.thSubText}>Linha 8</Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.thCenter}>
                        <Box sx={styles.thTitleBox}>
                          <span>Inoperante</span>
                          <Box component="span" sx={styles.thSubText}>Linha 9</Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredTrens.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5 + statusColumns.length} align="center" sx={styles.emptyCell}>
                          Nenhum trem encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTrens.map((trem) => (
                        <TableRow key={trem.id} hover sx={styles.tableRow}>
                          <TableCell sx={styles.tdName}>
                            <Box sx={styles.tdNameBox}>
                              <TrainIcon sx={styles.tdNameIcon} />
                              <Typography variant="body2" sx={styles.tdNameText}>
                                {trem.nome}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell align="center" sx={styles.tdCheck}>
                            <Checkbox
                              sx={styles.checkbox}
                              checked={alocacoes[trem.id]?.linha === '8'}
                              onChange={() => selectUnico(trem.id, 'linha', '8')}
                            />
                          </TableCell>
                          <TableCell align="center" sx={styles.tdCheck}>
                            <Checkbox
                              sx={styles.checkbox}
                              checked={alocacoes[trem.id]?.linha === '9'}
                              onChange={() => selectUnico(trem.id, 'linha', '9')}
                            />
                          </TableCell>
                          {statusColumns.map((s) => (
                            <TableCell key={s.id} align="center" sx={styles.tdCheck}>
                              <Checkbox
                                sx={styles.checkbox}
                                checked={alocacoes[trem.id]?.status === s.nome}
                                onChange={() => selectUnico(trem.id, 'status', s.nome)}
                              />
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={styles.tdCheck}>
                            <Checkbox
                              sx={styles.checkbox}
                              checked={alocacoes[trem.id]?.inoperante === '8'}
                              onChange={() => selectUnico(trem.id, 'inoperante', '8')}
                            />
                          </TableCell>
                          <TableCell align="center" sx={styles.tdCheck}>
                            <Checkbox
                              sx={styles.checkbox}
                              checked={alocacoes[trem.id]?.inoperante === '9'}
                              onChange={() => selectUnico(trem.id, 'inoperante', '9')}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Ações de salvar ou cancelar */}
            <Box sx={styles.actionsRow}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={handleConfirmar}
              >
                Confirmar
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </>
  );
}
